import { Bento } from "../components/Bento";
import TotalPosts from "./assets/total-posts.png";
import { t } from "@/i18n-js/instance";
import TotalLikes from "./assets/total-likes.png";

import TotalComments from "./assets/total-comments.png";

import { SanitizeHTMLString } from "@/react/components/shared/uikit/SanitizeContent";
import { abbreviateNumber } from "@/react/helpers/number";

import type { MemberEngagementMetrics } from "../../types";
import { ContributorAvatarStack } from "./ContributorAvatarStack";
import { MostPopularEvent } from "./MostPopularEvent";

const i18nRoot = "eoy_recap_2025.sections.members";

interface MembersProps {
  data?: MemberEngagementMetrics;
}

export const Members = ({ data }: MembersProps) => {
  if (!data) return null;

  return (
    <Bento
      id="member-engagement"
      accentColor="#3F107B"
      title={t([i18nRoot, "title"])}
    >
      <Bento.Item id="total-posts" baseColSpan={1} baseRowSpan={3}>
        <div className="flex flex-col h-full overflow-hidden">
          <div className="px-8 pt-8">
            <p className="text-4xl md:text-6xl font-bold">
              {abbreviateNumber(data.total_posts)}
            </p>
            <p className="text-base font-medium leading-none tracking-[-0.25px] text-white mb-4">
              {t([i18nRoot, "bento_items.total_post_created"])}
            </p>
          </div>
          <div className="size-full flex items-end">
            <img
              src={TotalPosts}
              alt={t([i18nRoot, "bento_items.total_post_created"])}
              className=""
              loading="lazy"
            />
          </div>
        </div>
      </Bento.Item>
      <Bento.Item id="total-courses-launched" baseColSpan={1} baseRowSpan={3}>
        <div className="relative flex flex-col h-full overflow-hidden p-8">
          <p className="text-center text-3xl font-bold leading-[100%] tracking-[-1.5px] text-white">
            <SanitizeHTMLString
              content={t([
                i18nRoot,
                "bento_items.top_contributor_or_most_active_member",
              ])}
            />
          </p>

          <ContributorAvatarStack contributors={data.top_contributors} />
        </div>
      </Bento.Item>
      <Bento.Item
        id="top-performing-post-or-content-piece"
        baseColSpan={1}
        baseRowSpan={3}
      >
        <div className="flex flex-col gap-4 h-full p-8 overflow-hidden">
          <p className="text-4xl font-bold mb-2 leading-8">
            <SanitizeHTMLString
              content={t([i18nRoot, "bento_items.most_popular_event"])}
            />
          </p>
          <MostPopularEvent event={data.most_popular_event} />
        </div>
      </Bento.Item>
      <Bento.Item
        id="most-watched-or-attended-event"
        baseColSpan={2}
        baseRowSpan={2}
      >
        <div className="flex flex-col md:flex-row justify-between size-full overflow-hidden p-8 gap-4">
          <div className="h-1/3 md:w-1/3 md:h-full">
            <p className="text-4xl md:text-6xl font-bold">
              {abbreviateNumber(data.total_comments)}
            </p>
            <p className="text-base font-medium leading-none tracking-[-0.25px] text-white mb-4">
              {t([i18nRoot, "bento_items.total_comments_created"])}
            </p>
          </div>
          <div className="h-2/3 md:w-2/3 md:h-full">
            <img
              src={TotalComments}
              alt={t([i18nRoot, "bento_items.total_comments_created"])}
              loading="lazy"
              className="object-cover size-full"
            />
          </div>
        </div>
      </Bento.Item>
      <Bento.Item id="new-members-joined" baseColSpan={1} baseRowSpan={2}>
        <div className="flex flex-col h-full items-start justify-between relative overflow-hidden rounded-2xl">
          <div className="flex flex-col p-8">
            <p className="text-4xl md:text-6xl font-bold">
              {abbreviateNumber(data.total_likes_given)}
            </p>
            <p className="text-base font-medium leading-none tracking-[-0.25px] text-white">
              {t([i18nRoot, "bento_items.total_likes_given"])}
            </p>
          </div>

          <img
            src={TotalLikes}
            alt={t([i18nRoot, "bento_items.new_members_joined"])}
            loading="lazy"
            className="w-[17rem] h-auto object-cover absolute top-0 -right-12"
          />
        </div>
      </Bento.Item>
    </Bento>
  );
};
